import { SyntheticEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IMatter } from '../../../interface/matter';
import { useFetchMatters } from '../../../hooks/useFetchMatters';
import { Heading, IconOutlineButton, Tooltip, Text } from '../../Ant';
import { Flex } from 'antd';
import MailMatterList from './mailMatterList';
import { ChevronDown, Refresh } from '../../icons';
import { useQueryClient } from '@tanstack/react-query';
import { MatterStatus } from '../../../constants/matter';
import { SuggestedMatterPanel } from '../../../assets/styles/components/dashboard.styled';

interface ISuggestedMattersProps {
  handleSelectMatter: (matter: IMatter, event: SyntheticEvent) => void;
  selectedMatterId?: string;
  emailRecipients: string[];
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}
const SuggestedMatters = ({
  handleSelectMatter,
  selectedMatterId,
  emailRecipients = [],
  expanded,
  setExpanded,
}: ISuggestedMattersProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const panelRef = useRef<HTMLDivElement>(null);
  const refocusRefreshRef = useRef(false);

  const {
    data: matterList,
    isLoading: isMatterLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMatters({
    sortBy: ['createdOn:DESC'],
    withFavoriteInformation: true,
    extraFilters: {
      relatedEmails: emailRecipients,
      status: MatterStatus.ACTIVE,
    },
    pageSize: 10,
    withDefaultTransactionType: true,
    withHasLinkedTransactionTypes: true,
    relations: ['principalCompany:name', 'principalPrivatePerson:fullName'],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'hasImportantNotes',
      'currency',
      'language',
      'createdOn',
    ],
  });

  const handleRefresh = () => {
    // The panel gives way to a loading row while refetching, taking the focused button with it.
    refocusRefreshRef.current = true;
    queryClient.removeQueries({
      queryKey: ['suggested-matters-list'],
    });
    refetch();
  };

  useEffect(() => {
    if (isMatterLoading || !refocusRefreshRef.current) return;
    refocusRefreshRef.current = false;
    panelRef.current?.querySelector<HTMLElement>('.suggested-matter-action')?.focus();
  }, [isMatterLoading]);

  if (isMatterLoading) {
    return (
      <Flex justify="space-between" align="center" className="pt-5 pb-gutter">
        <Text $fontsize="12px" $color="title" $lineheight="18px">
          {t('common:suggestedMatterLoadingText')}
        </Text>
        <div className="chat-bubble">
          <div className="typing">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      </Flex>
    );
  }

  if (!matterList || matterList.length === 0) {
    return null;
  }

  const toggleExpanded = () => setExpanded((prev: boolean) => !prev);

  const toggleOnActivationKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleExpanded();
  };

  const refreshWithoutToggling = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleRefresh();
  };

  const toggleProps = {
    role: 'button' as const,
    tabIndex: 0,
    'aria-expanded': expanded,
    onClick: toggleExpanded,
    onKeyDown: toggleOnActivationKey,
  };

  const headerContent = (
    <>
      <Heading
        $fontsize="12px"
        $lineheight="16px"
        $color="subtitle"
        $weight={500}
        className="ta-left"
      >
        {t('matter:suggestedMatter')}
      </Heading>
      <Flex align="center" gap={10} className="flex-shrink-0">
        {expanded && (
          <Tooltip title={t('common:refresh')} arrow={false}>
            <IconOutlineButton
              icon={<Refresh />}
              onClick={refreshWithoutToggling}
              aria-label={t('common:refresh')}
              className="suggested-matter-action"
              data-testid="refresh-button"
            />
          </Tooltip>
        )}
        <ChevronDown className="suggested-matter-chevron" />
      </Flex>
    </>
  );

  // The list stays mounted while collapsed so opening and closing it is one height animation.
  return (
    <SuggestedMatterPanel
      ref={panelRef}
      className={`mb-gutter${expanded ? ' expanded' : ''}`}
      data-testid="suggested-matters-panel"
    >
      <div {...toggleProps} className="suggested-matter-header">
        {headerContent}
      </div>
      <div className="suggested-matter-list">
        <MailMatterList
          matters={matterList}
          isLoading={isMatterLoading}
          isFetching={isFetching}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onSelectMatter={handleSelectMatter}
          selectedMatterId={selectedMatterId}
          data-testid="suggested-matters-list"
        />
      </div>
    </SuggestedMatterPanel>
  );
};

export default SuggestedMatters;
